import { MouseEvent, useState } from "react";
import { AppBar, Box, Container, Drawer, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Toolbar, Typography } from "@mui/material";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import HistoryEduOutlinedIcon from "@mui/icons-material/HistoryEduOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import { AxiosError } from "axios";
import { makeStyles } from "tss-react/mui";
import { useLocation, useNavigate } from "react-router";

import { logout } from "../../constants/api";
import { IApiErrorResponse } from "../../constants/api-interfaces";
import theme from "../../constants/theme";
import Logo from "../../images/logo_coloured.avif";
import StyledSvgIcon from "../../components/reusable/StyledSvgIcon";
import { canAccessDashboardRoute, type DashboardMenuKey } from "../../constants/utils";
import { useAuth } from "../../contexts/AuthContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { ReactComponent as MenuOpenSpoke } from "../../SVG/menu_open_spoke.svg";

type DashboardMenuEntry = {
  key: DashboardMenuKey;
  label: string;
  path: string;
  icon: typeof AccountCircleOutlinedIcon;
};

const useStyles = makeStyles()(() => ({
  logo: {
    height: "50px",
    minWidth: "150px",
    cursor: "pointer",
    overflow: "auto",
  },
}));

const dashboardMenu: DashboardMenuEntry[] = [
  {
    key: "docs",
    label: "Docs",
    path: "/docs",
    icon: DescriptionOutlinedIcon,
  },
  {
    key: "verify-users",
    label: "Verify Users",
    path: "/dashboard/verify-users",
    icon: VerifiedUserOutlinedIcon,
  },
  {
    key: "user-management",
    label: "Manage Users",
    path: "/dashboard/user-management",
    icon: ManageAccountsOutlinedIcon,
  },
  {
    key: "location-management",
    label: "Manage Locations",
    path: "/dashboard/location-management",
    icon: StorefrontOutlinedIcon,
  },
  {
    key: "access-logs",
    label: "Access Logs",
    path: "/dashboard/access-logs",
    icon: HistoryEduOutlinedIcon,
  },
];

export default function DashboardHeader() {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const { auth, authDispatch } = useAuth();
  const { snackbarDispatch } = useSnackbar();
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<HTMLElement | null>(null);
  const userFirstName = auth?.user?.firstName?.trim() || "User";

  const currentRole = auth?.user?.role ?? "teammember";
  const visibleDashboardMenu = dashboardMenu.filter((entry: DashboardMenuEntry) =>
    canAccessDashboardRoute(currentRole, entry.key),
  );

  const handleSelectMenu = (path: string) => {
    if (path === "/docs") {
      window.open('/docs/', '_blank', 'noopener,noreferrer')
    } else {
      navigate(path);
      setDrawerOpen(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      authDispatch({ type: "setAuth", payload: null });
      snackbarDispatch({ type: "setSnackbar", payload: { message: "Logged out successfully", error: false } });
      navigate("/login");
    } catch (error) {
      const axiosError = error as AxiosError<IApiErrorResponse>;
      authDispatch({ type: "setAuth", payload: null });
      snackbarDispatch({
        type: "setSnackbar",
        payload: { message: axiosError.response?.data?.message || "Logout failed", error: true },
      });
      navigate("/login");
    } finally {
      setDrawerOpen(false);
      setUserMenuAnchorEl(null);
    }
  };

  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setUserMenuAnchorEl(null);
  };

  const handleUserDetails = () => {
    handleCloseUserMenu();
    navigate("/dashboard/user-details");
  };

  return (
    <AppBar
      position="sticky"
      sx={{ background: "white", borderBottom: `2px solid ${theme.palette.primary.main}` }}
    >
      <Container maxWidth={false}>
        <Toolbar disableGutters sx={{ display: "flex", justifyContent: "space-between" }}>
          <img
            className={classes.logo}
            src={Logo}
            alt="Gyrosa logo"
            onClick={() => navigate("/")}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box
              onClick={handleOpenUserMenu}
              sx={{
                display: "flex",
                alignItems: "end",
                gap: 0.75,
                cursor: "pointer",
                px: 1,
                py: 0.5,
                borderRadius: 1,
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
              }}
            >
              <AccountCircleOutlinedIcon sx={{ fontSize: "30px", color: theme.palette.primary.main }} />
              <Typography
                variant="h3"
                sx={{
                  color: "text.primary",
                  fontWeight: 600,
                  letterSpacing: "0.02em",
                }}
              >
                {userFirstName}
              </Typography>
            </Box>
            <IconButton onClick={() => setDrawerOpen(true)}>
              <StyledSvgIcon
                component={MenuOpenSpoke}
                viewBox="0 0 1042 1650"
                sx={{ fontSize: "32px", color: theme.palette.primary.main, transform: "rotate(180deg)" }}
              />
            </IconButton>
          </Box>
        </Toolbar>
      </Container>

      <Menu
        anchorEl={userMenuAnchorEl}
        open={Boolean(userMenuAnchorEl)}
        onClose={handleCloseUserMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleUserDetails}>
          <ListItemIcon>
            <BadgeOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">User Details</Typography>
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Logout</Typography>
        </MenuItem>
      </Menu>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        anchor="right"
      >
        <List sx={{ p: 0, width: { xs: 250, md: 280 } }}>
          {visibleDashboardMenu.map((entry) => {
            const Icon = entry.icon;
            const selected = location.pathname === entry.path;

            return (
              <ListItem key={entry.key} disablePadding>
                <ListItemButton
                  sx={{ minHeight: 48, px: "8px" }}
                  onClick={() => handleSelectMenu(entry.path)}
                  selected={selected}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: 3,
                      justifyContent: "center",
                    }}
                  >
                    <Icon
                      sx={{
                        color: selected ? theme.palette.secondary.main : "text.primary",
                        fontSize: "42px",
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    sx={{
                      color: selected ? "primary.main" : "text.primary",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, textTransform: "uppercase" }}>
                      {entry.label}
                    </Typography>
                  </ListItemText>
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Drawer>
    </AppBar>
  );
}